import { useState, useEffect } from 'react';
import { Navigate, useSearchParams, useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';

import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useIsMobile } from '@/hooks/use-mobile';

import { AuthDesktop } from '@/components/auth/AuthDesktop';
import { AuthMobile } from '@/components/auth/AuthMobile';
import { AuthForm } from '@/components/auth/AuthForm';

const NATIVE_REDIRECT = 'clan://auth/callback';

const Auth = () => {
  console.log('Auth component loaded');
  const { user, loading, signIn, signUp } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [email, setEmail] = useState(searchParams.get('email') || '');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [familyRole, setFamilyRole] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [invitationDetails, setInvitationDetails] = useState<any>(null);
  const [isLoadingInvitation, setIsLoadingInvitation] = useState(false);
  const [checkingOnboarding, setCheckingOnboarding] = useState(false);

  const invitationToken = searchParams.get('invitation');
  const familyName = searchParams.get('family');
  const isMobile = useIsMobile();

  // Navigate when deep-link finished recreating a session
  useEffect(() => {
    const onSignedIn = () => {
      console.log('🧭 oauth:signedin → /dashboard');
      const pending = sessionStorage.getItem('pendingInvitation');
      if (pending) {
        sessionStorage.removeItem('pendingInvitation');
        processInvitation(pending).finally(() => navigate('/dashboard', { replace: true }));
      } else {
        navigate('/dashboard', { replace: true });
      }
    };
    window.addEventListener('oauth:signedin', onSignedIn);
    return () => window.removeEventListener('oauth:signedin', onSignedIn);
  }, [navigate]);

  // Load invitation details
  useEffect(() => {
    if (!invitationToken) return;
    (async () => {
      setIsLoadingInvitation(true);
      try {
        const { data, error } = await supabase.functions.invoke('get-invitation-details', {
          body: { token: invitationToken },
        });
        if (error) throw error;
        if (data) {
          setInvitationDetails(data);
          if (data.email) setEmail(data.email);
          if (data.personDetails) {
            setFirstName(data.personDetails.firstName || '');
            setFamilyRole(data.personDetails.familyRole || '');
          }
        }
      } catch (err) {
        console.error('Invitation details error:', err);
        toast({
          title: 'Invitation',
          description: "Impossible de charger les détails de l'invitation.",
          variant: 'destructive',
        });
      } finally {
        setIsLoadingInvitation(false);
      }
    })();
  }, [invitationToken, toast]);

  const processInvitation = async (token: string) => {
    try {
      const { error } = await supabase.functions.invoke('confirm-invitation', { body: { token } });
      if (error) {
        console.error('Error confirming invitation:', error);
        toast({
          title: 'Invitation',
          description: "Impossible d'accepter l'invitation. Le lien a peut-être expiré.",
          variant: 'destructive',
        });
      }
    } catch (err) {
      console.error('confirm-invitation failed:', err);
    }
  };

  // Web-only: Google OAuth callback handling (calendar connect)
  useEffect(() => {
    if (Capacitor.isNativePlatform()) return;

    const googleConnected = searchParams.get('google_connected');
    const googleError = searchParams.get('google_error');
    const accessToken = searchParams.get('access_token');
    const refreshToken = searchParams.get('refresh_token');
    const expiresIn = searchParams.get('expires_in');
    const tokenType = searchParams.get('token_type');

    if (googleConnected === 'true' && accessToken) {
      const tokens = {
        access_token: accessToken,
        refresh_token: refreshToken,
        expires_at: Date.now() + (parseInt(expiresIn || '3600', 10) * 1000),
        token_type: tokenType || 'Bearer',
      };
      localStorage.setItem('google_calendar_tokens', JSON.stringify(tokens));

      if (window.opener && window.opener !== window) {
        window.opener.postMessage({ type: 'GOOGLE_OAUTH_SUCCESS' }, '*');
        window.close();
      } else {
        toast({ title: 'Connexion réussie', description: 'Google Calendar connecté !' });
        navigate(user ? '/dashboard' : '/auth', { replace: true });
      }
      return;
    }

    if (googleError) {
      const messages: Record<string, string> = {
        access_denied: 'Vous avez annulé la connexion à Google Calendar.',
        invalid_request: 'Demande invalide. Veuillez réessayer.',
        server_error: 'Erreur serveur. Veuillez réessayer plus tard.',
      };
      toast({
        title: 'Erreur de connexion',
        description: messages[googleError] || "Une erreur s'est produite.",
        variant: 'destructive',
      });
      navigate(user ? '/dashboard' : '/auth', { replace: true });
    }
  }, [searchParams, navigate, toast, user]);

  // Auto-redirect when user becomes available
  useEffect(() => {
    if (!user || loading) {
      setCheckingOnboarding(false);
      return;
    }
    if (invitationToken) {
      void processInvitation(invitationToken);
      setCheckingOnboarding(false);
      return;
    }
    
    setCheckingOnboarding(true);
    
    (async () => {
      try {
        // Attendre que le profil soit créé (avec retry pour timing)
        let profile = null;
        let retries = 6;
        
        while (!profile && retries > 0) {
          const { data } = await supabase
            .from('profiles')
            .select('id, created_at')
            .eq('user_id', user.id)
            .maybeSingle();
          
          profile = data;
          
          if (!profile) {
            await new Promise(resolve => setTimeout(resolve, 800));
            retries--;
          }
        }

        // Vérifier les memberships dans tous les cas
        const { data: memberships } = await supabase
          .from('family_memberships')
          .select('family_id')
          .eq('user_id', user.id)
          .limit(1);

        // Si pas de memberships OU pas de profil = nouvel utilisateur → onboarding
        if (!memberships || memberships.length === 0 || !profile) {
          localStorage.removeItem('onboardingFamilyId');
          localStorage.removeItem('currentFamilyId');
          localStorage.setItem('onboardingMode', '1');
          sessionStorage.setItem('isNewUserSignup', 'true');
          console.log('🚀 New user detected (no memberships or profile not ready), redirecting to onboarding');
          setCheckingOnboarding(false);
          navigate('/new-user-onboarding', { replace: true });
          return;
        }

        // Si on arrive ici, l'utilisateur a des memberships ET un profil
        setCheckingOnboarding(false);
        navigate('/dashboard', { replace: true });
      } catch (error) {
        console.error('families check error:', error);
        setCheckingOnboarding(false);
        // En cas d'erreur, rediriger vers le dashboard qui fera sa propre vérification
        navigate('/dashboard', { replace: true });
      }
    })();
  }, [user, loading, invitationToken, navigate]);

  // Loading state
  if (loading || checkingOnboarding) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary" />
      </div>
    );
  }

  if (user && !checkingOnboarding) {
    if (invitationToken) {
      void processInvitation(invitationToken);
      return <Navigate to="/dashboard" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }

  // Email/password
  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    const { error } = await signIn(email, password);
    if (!error && invitationToken) await processInvitation(invitationToken);
    setIsLoading(false);
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    const { error } = await signUp(email, password, firstName, invitationToken || undefined);
    setIsLoading(false);
    if (!error && familyRole && invitationToken) {
      localStorage.setItem(`familyRole_${invitationToken}`, familyRole);
    }
  };

  const handleResendConfirmation = async () => {
    setResendLoading(true);
    const { error } = await supabase.auth.resend({
      type: 'signup',
      email,
      options: { emailRedirectTo: 'https://get-clan.com/' },
    });
    if (error) {
      toast({ title: 'Erreur', description: error.message, variant: 'destructive' });
    } else {
      toast({ title: 'Email envoyé', description: 'Un nouvel email de confirmation a été envoyé.' });
    }
    setResendLoading(false);
  };

  /** GOOGLE → opens provider & deep-links back to clan://auth/callback */
  const handleGoogleSignIn = async () => {
    setIsLoading(true);
    if (invitationToken) sessionStorage.setItem('pendingInvitation', invitationToken);

    try {
      const redirectBase = Capacitor.isNativePlatform() ? NATIVE_REDIRECT : `${window.location.origin}/auth`;
      const redirectTo = `${redirectBase}${invitationToken ? `?invitation=${encodeURIComponent(invitationToken)}` : ''}`;

      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo,
          skipBrowserRedirect: Capacitor.isNativePlatform(),
          // not mandatory, but common & harmless:
          queryParams: { access_type: 'offline', prompt: 'consent', ...(invitationToken ? { invitation: invitationToken } : {}) },
        },
      });
      if (error) throw error;

      if (Capacitor.isNativePlatform() && data?.url) {
        await Browser.open({ url: data.url });
      } else if (data?.url) {
        window.location.href = data.url;
      }
    } catch (e: any) {
      console.error('❌ Google OAuth error:', e);
      toast({ title: 'Erreur', description: e?.message || 'Connexion Google impossible.', variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  };

  /** APPLE → **force response_mode=query** so state & code are in the URL */
  const handleAppleSignIn = async () => {
    setIsLoading(true);
    if (invitationToken) sessionStorage.setItem('pendingInvitation', invitationToken);

    try {
      const redirectBase = Capacitor.isNativePlatform() ? NATIVE_REDIRECT : `${window.location.origin}/auth`;
      const redirectTo = `${redirectBase}${invitationToken ? `?invitation=${encodeURIComponent(invitationToken)}` : ''}`;

      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'apple',
        options: {
          redirectTo,
          skipBrowserRedirect: Capacitor.isNativePlatform(),
          // 👇 This is the key fix for native deep-link flows with Apple
          queryParams: {
            response_mode: 'query',
            ...(invitationToken ? { invitation: invitationToken } : {}),
          },
        },
      });
      if (error) throw error;

      if (Capacitor.isNativePlatform() && data?.url) {
        await Browser.open({ url: data.url });
      } else if (data?.url) {
        window.location.href = data.url;
      }
    } catch (e: any) {
      console.error('❌ Apple OAuth error:', e);
      toast({ title: 'Erreur', description: e?.message || 'Connexion Apple impossible.', variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  };

  const formProps = {
    email, setEmail,
    password, setPassword,
    firstName, setFirstName,
    familyRole, setFamilyRole,
    isLoading,
    showConfirmation, setShowConfirmation,
    resendLoading,
    invitationToken, invitationDetails,
    handleSignIn, handleSignUp,
    handleGoogleSignIn, handleAppleSignIn,
    handleResendConfirmation,
  };

  const layoutProps = { invitationToken, familyName, invitationDetails };

  return isMobile ? (
    <AuthMobile {...layoutProps}><AuthForm {...formProps} /></AuthMobile>
  ) : (
    <AuthDesktop {...layoutProps}><AuthForm {...formProps} /></AuthDesktop>
  );
};

export default Auth;
